import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue';
import { addDoc, collection, deleteDoc, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Plus, Search, Pencil, Trash2, X, Download } from 'lucide-vue-next';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { db } from '@/firebase';
const emptyForm = () => ({
    name: '',
    internalSku: '',
    supplierSku: '',
    suppliers: '',
    regularPrice: 0,
    stock: 0,
    lowStockThreshold: 0,
    category: ''
});
export default defineComponent({
    name: 'AdminInventory',
    components: { Plus, Search, Pencil, Trash2, X, Download },
    setup() {
        const products = ref([]);
        const selectedIds = ref([]);
        const searchText = ref('');
        const loading = ref(true);
        const pageError = ref('');
        const formError = ref('');
        const formOpen = ref(false);
        const saving = ref(false);
        const editingProduct = ref(null);
        const form = ref(emptyForm());
        let unsubscribe = null;
        const filteredProducts = computed(() => {
            const query = searchText.value.trim().toLowerCase();
            return products.value
                .filter(product => !query || [
                product.name,
                product.internalSku,
                product.supplierSku,
                product.category,
                ...product.suppliers
            ].some(value => value.toLowerCase().includes(query)))
                .sort((left, right) => left.name.localeCompare(right.name));
        });
        const allSelected = computed(() => filteredProducts.value.length > 0
            && filteredProducts.value.every(product => selectedIds.value.includes(product.id)));
        const stockValue = computed(() => products.value.reduce((total, product) => total + product.regularPrice * product.stock, 0));
        const formatMoney = (value) => new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            maximumFractionDigits: 2
        }).format(value);
        const exportInventoryPdf = () => {
            const rows = filteredProducts.value;
            if (rows.length === 0)
                return;
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const exportedAt = new Intl.DateTimeFormat('en-ZA', {
                dateStyle: 'medium',
                timeStyle: 'short'
            }).format(new Date());
            pdf.setTextColor(16, 39, 72);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(18);
            pdf.text('Inventory', 14, 16);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor(108, 120, 144);
            pdf.text(`${rows.length} items | Exported ${exportedAt}`, 14, 22);
            autoTable(pdf, {
                startY: 28,
                margin: { left: 14, right: 14, bottom: 14 },
                head: [[
                        'Name', 'Internal SKU', 'Supplier SKU', 'Supplier(s)', 'Regular Price',
                        'Current Stock', 'Low Stock Threshold', 'Category'
                    ]],
                body: rows.map(product => [
                    product.name,
                    product.internalSku || '-',
                    product.supplierSku || '-',
                    product.suppliers.join(', ') || '-',
                    formatMoney(product.regularPrice),
                    String(product.stock),
                    String(product.lowStockThreshold),
                    product.category || '-'
                ]),
                styles: { font: 'helvetica', fontSize: 8, cellPadding: 2.5, overflow: 'linebreak' },
                headStyles: { fillColor: [16, 39, 72], textColor: [255, 255, 255], fontStyle: 'bold' },
                alternateRowStyles: { fillColor: [246, 247, 252] },
                columnStyles: {
                    4: { halign: 'right' },
                    5: { halign: 'right' },
                    6: { halign: 'right' }
                }
            });
            pdf.save(`inventory-${new Date().toISOString().slice(0, 10)}.pdf`);
        };
        const mapProduct = (id, data) => ({
            id,
            name: String(data.name ?? ''),
            internalSku: String(data.internalSku ?? data.sku ?? ''),
            supplierSku: String(data.supplierSku ?? ''),
            suppliers: Array.isArray(data.suppliers)
                ? data.suppliers.map((supplier) => String(supplier))
                : String(data.suppliers ?? data.supplierName ?? '').split(',').map(value => value.trim()).filter(Boolean),
            regularPrice: Number(data.regularPrice ?? data.price ?? 0),
            stock: Number(data.stock ?? 0),
            lowStockThreshold: Number(data.lowStockThreshold ?? 0),
            category: String(data.category ?? '')
        });
        onMounted(() => {
            unsubscribe = onSnapshot(collection(db, 'inventory'), snapshot => {
                products.value = snapshot.docs.map(productDoc => mapProduct(productDoc.id, productDoc.data()));
                selectedIds.value = selectedIds.value.filter(id => products.value.some(product => product.id === id));
                loading.value = false;
            }, error => {
                console.error('Inventory subscription failed:', error);
                pageError.value = 'Could not load inventory. Check your connection and Firestore permissions.';
                loading.value = false;
            });
        });
        onUnmounted(() => unsubscribe?.());
        const toggleAll = (event) => {
            const checked = event.target.checked;
            const visibleIds = filteredProducts.value.map(product => product.id);
            selectedIds.value = checked
                ? Array.from(new Set([...selectedIds.value, ...visibleIds]))
                : selectedIds.value.filter(id => !visibleIds.includes(id));
        };
        const openCreateForm = () => {
            editingProduct.value = null;
            form.value = emptyForm();
            formError.value = '';
            formOpen.value = true;
        };
        const openEditForm = (product) => {
            editingProduct.value = product;
            form.value = {
                name: product.name,
                internalSku: product.internalSku,
                supplierSku: product.supplierSku,
                suppliers: product.suppliers.join(', '),
                regularPrice: product.regularPrice,
                stock: product.stock,
                lowStockThreshold: product.lowStockThreshold,
                category: product.category
            };
            formError.value = '';
            formOpen.value = true;
        };
        const closeForm = () => {
            if (saving.value)
                return;
            formOpen.value = false;
        };
        const saveProduct = async () => {
            saving.value = true;
            formError.value = '';
            const suppliers = form.value.suppliers.split(',').map(value => value.trim()).filter(Boolean);
            const price = Number(form.value.regularPrice);
            const stock = Number(form.value.stock);
            const lowStockThreshold = Number(form.value.lowStockThreshold);
            if (!Number.isFinite(price) || price < 0 || !Number.isInteger(stock) || stock < 0
                || !Number.isInteger(lowStockThreshold) || lowStockThreshold < 0) {
                formError.value = 'Enter a valid non-negative price, stock quantity, and threshold.';
                saving.value = false;
                return;
            }
            const productData = {
                name: form.value.name,
                internalSku: form.value.internalSku,
                supplierSku: form.value.supplierSku,
                suppliers,
                regularPrice: price,
                price,
                stock,
                lowStockThreshold,
                category: form.value.category,
                updatedAt: serverTimestamp()
            };
            try {
                if (editingProduct.value) {
                    await updateDoc(doc(db, 'inventory', editingProduct.value.id), productData);
                }
                else {
                    await addDoc(collection(db, 'inventory'), {
                        ...productData,
                        createdAt: serverTimestamp()
                    });
                }
                formOpen.value = false;
            }
            catch (error) {
                console.error('Saving inventory product failed:', error);
                formError.value = 'Could not save this product. Check Firestore permissions and try again.';
            }
            finally {
                saving.value = false;
            }
        };
        const deleteProduct = async (product) => {
            if (!window.confirm(`Delete ${product.name} from inventory?`))
                return;
            try {
                await deleteDoc(doc(db, 'inventory', product.id));
                selectedIds.value = selectedIds.value.filter(id => id !== product.id);
            }
            catch (error) {
                console.error('Deleting inventory product failed:', error);
                pageError.value = 'Could not delete the product. Check Firestore permissions and try again.';
            }
        };
        return {
            products,
            filteredProducts,
            selectedIds,
            searchText,
            loading,
            pageError,
            formError,
            formOpen,
            saving,
            editingProduct,
            form,
            allSelected,
            stockValue,
            formatMoney,
            exportInventoryPdf,
            toggleAll,
            openCreateForm,
            openEditForm,
            closeForm,
            saveProduct,
            deleteProduct
        };
    },
});
